import HTTP from '../libs/HTTP';

export interface ConsultantQueryParam {
    id?: number;
    name?: string;
    expertiseKeyword?: string;
    states?: number;
    includeDeleted?: boolean;
}

export interface ConsultantListRequest {
    queryParam: ConsultantQueryParam;
    pageNo: number;
    pageSize: number;
}

export interface IConsultantInfo {
    id: number;
    name: string;
    idCardNo?: string | null;
    residenceCity?: string | null;
    languageTags?: string | null;
    feeDay?: number | null;
    feeHalfDay?: number | null;
    feeOnlineHour?: number | null;
    feeCoachHour?: number | null;
    feeItems?: string | null;
    feeSpecialRemark?: string | null;
    paymentMode?: string | null;
    expertise?: string | null;
    states: number;
    attachmentCount?: number;
    createTime?: number;
    lastEditTime?: number;
}

export interface IConsultantInfoList {
    records: IConsultantInfo[];
    total: number;
    size: number;
    current: number;
    pages: number;
}

export interface IConsultantAttachment {
    id: number;
    consultantId: number;
    fileName: string;
    fileUrl: string;
    fileExt?: string;
    fileSize?: number;
    createTime?: number;
}

export interface IConsultantExpertiseTag {
    id: string;
    label: string;
    category?: string;
    source?: 'MANUAL' | 'AI' | string;
    reviewStatus?: 'APPROVED' | 'PENDING' | string;
    reviewRecommendation?: string;
    aiModel?: string;
    aiReason?: string;
    aiConfidence?: number;
    aiConfidenceScore?: number;
    aiDataSources?: string;
    aiReasoningProcess?: string;
    promptTemplateId?: number;
    promptVersion?: number;
    createTime?: string;
    lastEditTime?: string;
}

export interface ConsultantPostParam {
    name: string;
    idCardNo?: string;
    residenceCity?: string;
    languageTags?: string;
    feeDay?: number;
    feeHalfDay?: number;
    feeOnlineHour?: number;
    feeCoachHour?: number;
    feeItems?: string;
    feeSpecialRemark?: string;
    paymentMode?: string;
    expertise?: string;
    states?: number;
}

export interface ConsultantPutParam extends ConsultantPostParam {
    id: number;
}

class ConsultantService extends HTTP {
    async getConsultantList(request: ConsultantListRequest) {
        const response = await this.post('/consultant/getConsultant', request.queryParam, {
            params: {
                pageNo: request.pageNo,
                pageSize: request.pageSize,
            },
        });
        return response ? response.data : null;
    }

    async getConsultantById(id: number) {
        const response = await this.get(`/consultant/getConsultantById/${id}`);
        return response ? response.data : null;
    }

    async getAttachmentByConsultantId(consultantId: number) {
        const response = await this.get(`/consultant/getAttachmentByConsultantId/${consultantId}`);
        return response ? response.data : null;
    }

    async addConsultant(request: ConsultantPostParam) {
        const response = await this.post('/consultant/addConsultant', request);
        return response ? response.data : null;
    }

    async updateConsultant(request: ConsultantPutParam) {
        const response = await this.put(`/consultant/updateConsultant/${request.id}`, request);
        return response ? response.data : null;
    }

    async deleteConsultant(id: number) {
        const response = await this.delete(`/consultant/deleteConsultant/${id}`);
        return response ? response.data : null;
    }

    async terminateConsultant(id: number) {
        const response = await this.put(`/consultant/updateConsultant/${id}`, { states: 1 });
        return response ? response.data : null;
    }

    async uploadAttachment(consultantId: number, file: File) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await this.post(`/consultant/uploadAttachment/${consultantId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response ? response.data : null;
    }

    async deleteAttachment(attachmentId: number) {
        const response = await this.delete(`/consultant/deleteAttachment/${attachmentId}`);
        return response ? response.data : null;
    }

    async identifyAiTags(consultantId: number) {
        const response = await this.post(`/consultant/ai/identifyTags/${consultantId}`);
        return response ? response.data : null;
    }

    async approveAiTag(consultantId: number, tagId: string) {
        const response = await this.post(`/consultant/ai/approveTag/${consultantId}`, { tagId });
        return response ? response.data : null;
    }

    async rejectAiTag(consultantId: number, tagId: string, reason?: string, suggestion?: string) {
        const response = await this.post(`/consultant/ai/rejectTag/${consultantId}`, {
            tagId,
            reason,
            suggestion,
        });
        return response ? response.data : null;
    }
}

export default new ConsultantService();
